import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  CheckBadgeIcon,
  ShieldCheckIcon,
  XCircleIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  BellIcon,
  LinkIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/solid";
import Navbar from "../components/Navbar";
import { getMyProfile, updateMyProfile, getMyActivity } from "../api/usersApi";
import { getPortfolioSummary } from "../api/portfolioApi";
import {
  formatCurrency,
  resolveDisplayCurrency,
  sumFieldInCurrency,
  totalInCurrency,
} from "../utils/currency";
import { fileToSquareDataURL } from "../utils/image";

const ACTIVITY_ICON = {
  chat: ChatBubbleLeftRightIcon,
  insight: SparklesIcon,
  notification: BellIcon,
  sync: LinkIcon,
  manual_asset: PlusCircleIcon,
};

const formatDate = (iso, opts) =>
  new Date(iso).toLocaleString(
    undefined,
    opts ?? { month: "short", day: "numeric", year: "numeric" },
  );

const relativeTime = (iso) => {
  const then = new Date(iso).getTime();
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return formatDate(iso);
};

// Each completeness check links the user straight to the Settings section
// that owns that field, so a partial profile is one click from being fixed.
const COMPLETENESS_CHECKS = [
  { key: "first_name", label: "First name", section: "account" },
  { key: "last_name", label: "Last name", section: "account" },
  { key: "country", label: "Country", section: "account" },
  { key: "bio", label: "Bio", section: "account" },
  { key: "avatar_url", label: "Profile photo", section: "account" },
  { key: "tax_jurisdiction", label: "Tax jurisdiction", section: "investing" },
  { key: "risk_tolerance", label: "Risk tolerance", section: "investing" },
  { key: "display_currency", label: "Display currency", section: "account" },
  { key: "timezone", label: "Timezone", section: "account" },
  { key: "email_verified", label: "Email verified", section: "security", boolean: true },
];

const profileCompleteness = (p) => {
  if (!p) return { percent: 0, missing: [] };
  const missing = COMPLETENESS_CHECKS.filter((c) =>
    c.boolean ? !p[c.key] : !((p[c.key] || "") + "").trim(),
  );
  const percent = Math.round(
    ((COMPLETENESS_CHECKS.length - missing.length) / COMPLETENESS_CHECKS.length) * 100,
  );
  return { percent, missing };
};

const Badge = ({ ok, label, IconOk = CheckBadgeIcon, IconNo = XCircleIcon }) => {
  const Icon = ok ? IconOk : IconNo;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
        ok
          ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30"
          : "bg-gray-500/10 text-gray-500 border border-gray-500/30"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
};

const KpiCard = ({ label, value, accent = "text-indigo-500" }) => (
  <div className="p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
    <p className="text-[10px] font-bold tracking-widest text-gray-500 dark:text-gray-400 uppercase mb-2">
      {label}
    </p>
    <p className={`text-2xl sm:text-3xl font-extrabold ${accent}`}>{value}</p>
  </div>
);

const EditModal = ({ open, onClose, profile, onSaved }) => {
  const [draft, setDraft] = useState(() => ({
    first_name: profile?.first_name ?? "",
    last_name: profile?.last_name ?? "",
    username: profile?.username ?? "",
    bio: profile?.bio ?? "",
    country: profile?.country ?? "",
    avatar_url: profile?.avatar_url ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({
      first_name: profile?.first_name ?? "",
      last_name: profile?.last_name ?? "",
      username: profile?.username ?? "",
      bio: profile?.bio ?? "",
      country: profile?.country ?? "",
      avatar_url: profile?.avatar_url ?? "",
    });
    setError("");
  }, [open, profile]);

  const onPhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("Photo is too large. Pick something under 10 MB.");
      return;
    }
    setPhotoBusy(true);
    setError("");
    try {
      const dataUrl = await fileToSquareDataURL(file, { size: 256, quality: 0.85 });
      setDraft((d) => ({ ...d, avatar_url: dataUrl }));
    } catch (err) {
      setError(err?.message || "Could not process that image.");
    } finally {
      setPhotoBusy(false);
    }
  };

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const updated = await updateMyProfile(draft);
      onSaved?.(updated);
      onClose();
    } catch (err) {
      const data = err.response?.data || {};
      const firstField = Object.keys(data)[0];
      setError(
        firstField
          ? `${firstField}: ${Array.isArray(data[firstField]) ? data[firstField][0] : data[firstField]}`
          : "Could not save profile.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">Edit profile</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-red-500">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Photo */}
        <div className="flex items-center gap-4">
          <img
            src={
              draft.avatar_url ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                `${draft.first_name || ""} ${draft.last_name || draft.username || "U"}`,
              )}&background=4f46e5&color=fff&size=128`
            }
            alt="avatar preview"
            className="w-20 h-20 rounded-2xl object-cover border-2 border-indigo-500/40"
          />
          <div className="flex-1">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold cursor-pointer">
              {photoBusy ? "Processing..." : "Upload photo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPhotoChange}
                disabled={photoBusy}
              />
            </label>
            {draft.avatar_url && (
              <button
                type="button"
                onClick={() => setDraft({ ...draft, avatar_url: "" })}
                className="ml-2 text-xs font-semibold text-gray-500 hover:text-red-500"
              >
                Remove
              </button>
            )}
            <p className="text-[11px] text-gray-500 mt-1">
              Resized to 256×256 in your browser before saving.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">First name</label>
            <input
              value={draft.first_name}
              onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Last name</label>
            <input
              value={draft.last_name}
              onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Username</label>
          <input
            value={draft.username}
            onChange={(e) => setDraft({ ...draft, username: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Country (ISO-2)</label>
          <input
            value={draft.country}
            onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase().slice(0, 2) })}
            placeholder="IN"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Bio</label>
          <textarea
            rows={3}
            value={draft.bio}
            onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
            placeholder="What kind of investor are you?"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {error && (
          <div className="p-2 rounded-md bg-red-500/10 text-red-500 text-sm">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-white font-semibold ${saving ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"}`}
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Profile = () => {
  const { refreshProfile } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const refresh = useCallback(async () => {
    const [p, port, act] = await Promise.allSettled([
      getMyProfile(),
      getPortfolioSummary(),
      getMyActivity(),
    ]);
    if (p.status === "fulfilled") setProfile(p.value);
    if (port.status === "fulfilled") setPortfolio(port.value);
    if (act.status === "fulfilled") setActivity(act.value.activity || []);
    // Push the latest profile (incl. avatar_url) into AuthContext so the
    // navbar avatar refreshes without a hard reload.
    refreshProfile?.();
  }, [refreshProfile]);

  useEffect(() => {
    (async () => {
      try {
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  const displayName = useMemo(() => {
    if (!profile) return "";
    const fn = (profile.first_name || "").trim();
    const ln = (profile.last_name || "").trim();
    if (fn || ln) return `${fn} ${ln}`.trim();
    return profile.username;
  }, [profile]);

  const { percent: completeness, missing: missingFields } = profileCompleteness(profile);

  const kpis = useMemo(() => {
    if (!portfolio) return [];
    // Convert every holding into the user's preferred display currency so
    // a mix of INR + USD positions sums correctly under one symbol.
    const displayCurrency = resolveDisplayCurrency(profile);
    const holdings = portfolio.holdings || [];
    const totalValue = totalInCurrency(holdings, displayCurrency);
    const totalCost = sumFieldInCurrency(holdings, "costBasis", displayCurrency);
    const totalReturn = totalValue - totalCost;
    const totalReturnPct =
      totalCost > 0 ? (totalReturn / totalCost) * 100 : portfolio.totalReturnPercent ?? 0;
    return [
      {
        label: "Portfolio value",
        value: holdings.length ? formatCurrency(totalValue, displayCurrency) : "—",
        accent: "text-indigo-500",
      },
      {
        label: "Total return",
        value: holdings.length
          ? `${totalReturnPct >= 0 ? "+" : ""}${totalReturnPct.toFixed(2)}%`
          : "—",
        accent: totalReturnPct >= 0 ? "text-emerald-500" : "text-red-500",
      },
      {
        label: "Risk score",
        value: portfolio.riskLabel
          ? `${portfolio.riskLabel} (${portfolio.riskScore}/10)`
          : "—",
        accent: "text-amber-500",
      },
      {
        label: "Holdings",
        value: portfolio.numberOfHoldings ?? "—",
        accent: "text-sky-500",
      },
    ];
  }, [portfolio, profile]);

  const avatarUrl =
    profile?.avatar_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || "U")}&background=4f46e5&color=fff&size=160`;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white">
      <Navbar />

      <div className="max-w-5xl mx-auto pt-28 px-6 pb-24 space-y-8 animate-fadeIn">
        {/* ====================== HERO ====================== */}
        <section className="p-6 sm:p-8 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
          <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-24 h-24 rounded-2xl object-cover border-2 border-indigo-500/40"
            />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold truncate">
                  {loading ? "Loading…" : displayName || "Set your name"}
                </h1>
                <button
                  onClick={() => setEditOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  <PencilSquareIcon className="w-3.5 h-3.5" />
                  Edit
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                @{profile?.username} · {profile?.email}
              </p>
              {profile?.date_joined && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Member since {formatDate(profile.date_joined, { month: "long", year: "numeric" })}
                  {profile?.country ? ` · ${profile.country}` : ""}
                </p>
              )}
              {profile?.bio && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-3">
                  {profile.bio}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                {/* Email verification: always shown — reflects truth either way */}
                <Badge ok={!!profile?.email_verified} label="Email verified" />
                {/* Phone: only relevant once a number is on file */}
                {profile?.phone ? (
                  <Badge ok={!!profile?.phone_verified} label="Phone verified" />
                ) : null}
                {/* 2FA: only show when actually on. Hide for everyone else
                    so we don't pretend at a feature that isn't enabled. */}
                {profile?.two_factor_enabled ? (
                  <Badge ok label="2FA on" IconOk={ShieldCheckIcon} />
                ) : null}
                {/* Auth provenance — meaningful when verified */}
                {profile?.auth_provider === "google" ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-500/15 text-blue-500 border border-blue-500/30">
                    <svg className="w-3 h-3" viewBox="0 0 48 48" fill="currentColor">
                      <path d="M44.5 20H24v8.5h11.7C34.5 33.4 30 36.5 24 36.5c-7 0-12.5-5.7-12.5-12.5S17 11.5 24 11.5c3 0 5.7 1 7.8 2.7l6.4-6.4C34.7 4.5 29.7 2.5 24 2.5 12 2.5 2.5 12 2.5 24S12 45.5 24 45.5c11.5 0 21-8.4 21-21 0-1.5-.1-2.9-.5-4.5z"/>
                    </svg>
                    Connected via Google
                  </span>
                ) : null}
                {profile?.tax_jurisdiction && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-sky-500/15 text-sky-500 border border-sky-500/30">
                    Tax: {profile.tax_jurisdiction}
                  </span>
                )}
                {profile?.risk_tolerance && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-500/15 text-purple-500 border border-purple-500/30">
                    Risk: {profile.risk_tolerance}
                  </span>
                )}
              </div>
            </div>

            {/* Profile completeness — each missing field deep-links into the
                exact Settings tab that owns it. */}
            <div className="w-full sm:w-56 shrink-0">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2">
                Profile {completeness}%
              </p>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-indigo-500 transition-all"
                  style={{ width: `${completeness}%` }}
                />
              </div>
              {missingFields.length === 0 ? (
                <p className="mt-2 text-[11px] text-emerald-500 font-semibold">
                  Profile complete ✓
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {missingFields.slice(0, 5).map((m) => (
                    <Link
                      key={m.key}
                      to={`/settings?tab=${m.section}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-500 text-[10px] font-semibold hover:bg-indigo-500/20"
                    >
                      + {m.label}
                    </Link>
                  ))}
                  {missingFields.length > 5 && (
                    <span className="text-[10px] text-gray-500 self-center">
                      +{missingFields.length - 5} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ====================== KPI STRIP ====================== */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <KpiCard key={k.label} {...k} />
          ))}
        </section>

        {/* ====================== ACTIVITY FEED ====================== */}
        <section className="p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
          <h2 className="text-lg font-bold mb-4">Recent activity</h2>
          {activity.length === 0 ? (
            <p className="text-sm text-gray-500">
              No activity yet. Ask the Council a question, link an account, or add a manual asset to populate this feed.
            </p>
          ) : (
            <ul className="divide-y divide-gray-200/60 dark:divide-white/5">
              {activity.map((item, i) => {
                const Icon = ACTIVITY_ICON[item.kind] || SparklesIcon;
                return (
                  <li key={i} className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 truncate">
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                        {relativeTime(item.at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ====================== QUICK LINKS ====================== */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/settings?tab=connected"
            className="p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 hover:-translate-y-0.5 transition shadow-glass dark:shadow-glass-dark"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Connected accounts
            </p>
            <p className="font-bold mt-1">Manage brokerages →</p>
          </Link>
          <Link
            to="/settings"
            className="p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 hover:-translate-y-0.5 transition shadow-glass dark:shadow-glass-dark"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Preferences
            </p>
            <p className="font-bold mt-1">Settings →</p>
          </Link>
          <Link
            to="/portfolio"
            className="p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 hover:-translate-y-0.5 transition shadow-glass dark:shadow-glass-dark"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Portfolio
            </p>
            <p className="font-bold mt-1">Today's brief →</p>
          </Link>
        </section>
      </div>

      <EditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        profile={profile}
        onSaved={() => refresh()}
      />
    </div>
  );
};

export default Profile;
